/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useFormikContext } from "formik";
import { FC, useEffect, useRef } from "react";

import { DatepickerFormik } from "web/Layout/Datepicker";
import {
  BIRTH_DATE_MAX,
  BIRTH_DATE_MIN,
} from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/ActiveForm/constants";
import __ from "web/Layout/Translations";

import Checkbox from "web/Components/Common/Form/Checkbox";
import Field from "web/Components/Common/Form/Field";

import { parseBirthDateFromPesel } from "web/utils/parseBirthDateFromPesel";
import { peselMask } from "web/utils/peselMask";

import type { Classes, PropsWithClasses } from "web/types/Common";
import type {
  ISubscriptionReceiverFormValues,
  RequirePeselField,
  RequirePeselValue,
} from "web/types/Subscription";

interface IRequirePeselFormProps {
  field: RequirePeselField;
  checkboxClass: Classes;
  tooltipMessage?: string;
  disabled?: boolean;
  lang?: {
    key: string;
    code: string;
  };
}

const labels = {
  pl: {
    babyOrForeigner: "Obcokrajowiec / nowonarodzone dziecko",
    pesel: "Pesel",
    birthDate: "Data urodzenia",
  },
  en: {
    babyOrForeigner: "Foreigner/newborn baby",
    pesel: "Pesel number",
    birthDate: "Date of birth",
  },
};

const RequirePeselForm: FC<PropsWithClasses<IRequirePeselFormProps>> = ({
  field,
  checkboxClass,
  classes = {},
  tooltipMessage,
  disabled = false,
  lang = { key: "pl_PL", code: "pl" },
}) => {
  const { values, setFieldValue } =
    useFormikContext<ISubscriptionReceiverFormValues>();

  // Używamy ref do śledzenia poprzedniej wartości PESEL
  const prevPeselRef = useRef<string>("");

  // Automatyczne uzupełnianie daty urodzenia z PESEL
  useEffect(() => {
    const fieldValue = values[field.id] as RequirePeselValue | undefined;
    const peselValue = fieldValue?.pesel || "";
    const babyOrForeigner = fieldValue?.babyOrForeigner;

    // Blokada automatycznego wypełniania gdy zaznaczony jest checkbox "babyOrForeigner"
    if (babyOrForeigner) {
      prevPeselRef.current = peselValue;
      return;
    }

    // Automatycznie wypełnij lub wyczyść gdy PESEL się zmienił
    const peselChanged = peselValue !== prevPeselRef.current;

    if (peselChanged) {
      if (peselValue) {
        const birthDateTimestamp = parseBirthDateFromPesel(peselValue);

        if (birthDateTimestamp !== null) {
          // Ustaw datę urodzenia - zawsze gdy PESEL jest poprawny
          setFieldValue(`${field.id}.birthDate`, birthDateTimestamp);
        }
      } else {
        // Wyczyść datę urodzenia gdy PESEL został usunięty
        setFieldValue(`${field.id}.birthDate`, null);
      }
    }

    // Zapisz aktualny PESEL jako poprzedni
    prevPeselRef.current = peselValue;
  }, [values, field.id, setFieldValue]);

  return (
    <>
      <Checkbox
        uppercase
        name={`${field.id}.babyOrForeigner`}
        classes={checkboxClass}
        tooltipMessage={tooltipMessage}
        isDisabled={disabled}
      >
        <label
          htmlFor={`${field.id}.babyOrForeigner`}
          className={`${classes.label} ${classes.checkboxLabel}`}
        >
          {__(labels[lang.code as keyof typeof labels].babyOrForeigner)}
        </label>
      </Checkbox>
      <label htmlFor={`${field.id}.pesel`} className={classes.label}>
        {__(labels[lang.code as keyof typeof labels].pesel)}
      </label>
      <Field
        // @ts-ignore
        id={`${field.id}.pesel`}
        name={`${field.id}.pesel`}
        mask={peselMask}
        disabled={disabled}
      />
      <label htmlFor={`${field.id}.birthDate`} className={classes.label}>
        {__(labels[lang.code as keyof typeof labels].birthDate)}
      </label>
      <div className={`${classes.inputWrapper} ${classes.dataInputWrapper}`}>
        <DatepickerFormik
          name={`${field.id}.birthDate`}
          // @ts-ignore
          label={field.label}
          placeholder={__(labels[lang.code as keyof typeof labels].birthDate)}
          className={classes.input}
          minDate={BIRTH_DATE_MIN}
          maxDate={BIRTH_DATE_MAX}
          disabled={disabled}
        />
      </div>
    </>
  );
};

export default RequirePeselForm;
